<script setup lang="ts">
import { tokenService } from '~/api/service/token.service'
import IconButton from '~/components/button/IconButton.vue'
import AddressCopyLink from '~/components/link/AddressCopyLink.vue'
import { useCopy } from '~/composables/useCopy'
import { useResponsiveScreen } from '~/composables/useResponsiveScreen'
import { useTokenType } from '~/composables/useTokenType'
import { TOKEN_TYPE_NAME } from '~/constants/token.constants'
import { TOKEN_UNIT } from '~/environment-variables/unit.const'
import { INftType } from '~/types/token-type.type'
import BcLabelContainer from '~/ui/LabelContainer/BcLabelContainer.vue'
import { getRouteParam } from '~/utils/route.util'

const { tokenTypeValue, isErc1155 } = useTokenType<INftType>()
const { isMobile } = useResponsiveScreen()
const { copy } = useCopy()

const address = getRouteParam('address')
const tokenId = computed(() => getRouteParam('tokenId'))
const isItemPage = computed(() => !!tokenId.value)

const { data } = tokenService.useGetTokenDetail(address, tokenTypeValue)
</script>

<template>
  <div
    v-if="data"
    class="summary-container"
    :class="[isItemPage ? 'grid-cols-1' : 'grid-cols-2']"
  >
    <div class="info-container">
      <Chip
        :value="TOKEN_TYPE_NAME[data.tokenType]"
        class="w-fit text-base"
      />
      <div class="mb-4 mt-2 flex w-full items-center gap-x-4">
        <img
          :src="data.tokenInfo.logo"
          :alt="`${data.tokenInfo.name} symbol image`"
          width="48"
          height="48"
          class="rounded"
        >
        <p class="token-name">
          <template v-if="data.tokenName.length">
            <template
              v-for="(name, index) in data.tokenName"
              :key="index"
            >
              <span
                v-if="!name.isEmoji"
                class="word"
              >
                {{ name.str }}
              </span>
              <template v-else>
                {{ name.str }}
              </template>
            </template>
            <span
              v-if="data.tokenInfo.symbol"
              class="word pl-1"
            >({{ data.tokenInfo.symbol }})</span>
          </template>
        </p>
      </div>

      <div
        v-if="isItemPage"
        class="flex items-center gap-x-1"
      >
        <span class="truncate">#{{ tokenId }}</span>
        <IconButton
          icon="copy"
          @click="copy(tokenId)"
        />
      </div>

      <AddressCopyLink
        v-else
        :address="data.tokenInfo.address"
        class="truncate"
        :line-break="isMobile"
      />
    </div>

    <div
      v-if="!isItemPage"
      class="info-container"
    >
      <div class="total-container">
        <BcLabelContainer
          label="Total Supply"
          label-size="base"
          gap-y="gap-y-1"
          :tooltip="isErc1155 && 'The total supply is updated every 10 minutes.'"
        >
          <template #content>
            <span
              class="token-info"
              v-tooltip="data.totalSupply"
            >{{
              isMobile ? data.totalSupplyAbbr : data.totalSupply
            }}</span>
          </template>
        </BcLabelContainer>
        <BcLabelContainer
          label="Total Transfers"
          label-size="base"
          gap-y="gap-y-1"
        >
          <template #content>
            <span
              class="token-info"
              v-tooltip="data.totalTransfers"
            >{{
              isMobile ? data.totalTransfersAbbr : data.totalTransfers
            }}</span>
          </template>
        </BcLabelContainer>
        <BcLabelContainer
          label="Holders"
          label-size="base"
          gap-y="gap-y-1"
        >
          <template #content>
            <span
              class="token-info"
              v-tooltip="data.totalHolders"
            >{{ isMobile ? data.totalHoldersAbbr : data.totalHolders }}</span>
          </template>
        </BcLabelContainer>
        <BcLabelContainer
          label="Min Price"
          label-size="base"
          gap-y="gap-y-1"
        >
          <template #content>
            <span class="token-info">{{ data.minPrice }}<span class="text-primary/40">{{ TOKEN_UNIT }}</span></span>
          </template>
        </BcLabelContainer>
        <BcLabelContainer
          label="Max Price"
          label-size="base"
          gap-y="gap-y-1"
        >
          <template #content>
            <span class="token-info">{{ data.maxPrice }}<span class="text-primary/40">{{ TOKEN_UNIT }}</span></span>
          </template>
        </BcLabelContainer>
        <BcLabelContainer
          label="Volume (24H)"
          label-size="base"
          gap-y="gap-y-1"
        >
          <template #content>
            <span class="token-info">{{ data.volume }}<span class="text-primary/40">{{ TOKEN_UNIT }}</span></span>
          </template>
        </BcLabelContainer>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.summary-container {
  @apply grid text-primary/40 gap-x-4;

  @include sm {
    @apply grid-cols-1 gap-y-4;
  }

  .info-container {
    @apply flex flex-col;

    .token-name {
      @apply font-bold text-3xl leading-tight truncate text-primary;

      @include sm {
        @apply text-2xl;
      }
    }

    .total-container {
      @apply grid grid-cols-3 gap-x-1 gap-y-4;
    }

    .token-info {
      @apply flex items-center gap-x-1 text-lg leading-normal text-primary truncate max-w-fit;
    }
  }
}
</style>